/**
 * Workspace Closed Overlay
 *
 * SUITE STANDARD v2.3.0:
 * - Full-screen, non-dismissible overlay
 * - Shown when 409 WORKSPACE_CLOSED received (at boot or mid-session)
 * - otherWorkspaces ALWAYS populated (from switch-context re-fetch)
 * - Forces workspace switch; no close button, no escape, no click-outside
 * - Switch flow uses selectSessionWorkspace (OAuth cookie mutation) + OIDC re-auth
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Building2, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { RootState } from '../../store';
import { BootWorkspace } from '../../store/slices/bootSlice';
import { selectSessionWorkspace } from '../../lib/centralSession';
import { startWorkspaceSwitch } from '../../lib/centralAuth';

export function WorkspaceClosedOverlay() {
  const closedWorkspaceName = useSelector(
    (state: RootState) => state.boot.closedWorkspaceName
  );
  const closedOtherWorkspaces = useSelector(
    (state: RootState) => state.boot.closedOtherWorkspaces
  );

  const workspaceName = closedWorkspaceName ?? 'your workspace';
  const otherWorkspaces: BootWorkspace[] = closedOtherWorkspaces ?? [];

  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSwitchWorkspace = async (workspaceId: string) => {
    setIsSwitching(true);
    setError(null);
    try {
      const result = await selectSessionWorkspace(workspaceId);
      if (!result.ok) {
        setError(result.error ?? 'Failed to select workspace');
        setIsSwitching(false);
        return;
      }
      await startWorkspaceSwitch(workspaceId);
      // Page redirects — no state reset needed
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch workspace');
      setIsSwitching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 bg-opacity-90">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
          <Building2 className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Workspace Closed
        </h2>
        <p className="text-gray-600 mb-6">
          <span className="font-medium">{workspaceName}</span> has been closed
          and is no longer accessible.
        </p>

        {otherWorkspaces.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-3">
              Switch to another workspace:
            </p>
            {otherWorkspaces.map((ws) => (
              <button
                key={ws.id}
                onClick={() => handleSwitchWorkspace(ws.id)}
                disabled={isSwitching}
                className={clsx(
                  'w-full flex items-center justify-between px-4 py-3',
                  'border border-gray-200 rounded-lg',
                  'hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait',
                  'transition-colors'
                )}
              >
                <span className="font-medium text-gray-900">{ws.name}</span>
                <span className="text-xs text-gray-500 capitalize">
                  {ws.role}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            You have no other workspaces available. Contact your administrator.
          </p>
        )}

        {isSwitching && (
          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Switching workspace...
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
